import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useTime } from './TimeContext'
import {
  isLocalNotificationsAvailable,
  requestLocalNotificationPermission,
  checkLocalNotificationPermission,
  buildTaskNotifications,
  buildMedicationNotificationsForDays,
  buildSnoozeNotifications,
  pruneExpiredSnoozes,
  registerMedicationActionTypes,
  SNOOZE_OPTIONS,
  scheduleAllNotifications,
  cancelAllLocalNotifications,
  addLocalNotificationListeners,
} from '@/utils/localNotifications'

const NotificationContext = createContext(null)

const STORAGE_KEY = 'adhd-notifications-enabled'
const MED_STORAGE_KEY = 'adhd-med-reminders-enabled'
// Persisted map of active snoozes: { [medId]: snoozeUntilEpochMs }. Kept in
// storage so a snoozed reminder survives the cancel-and-rebuild that every
// reschedule performs.
const SNOOZE_STORAGE_KEY = 'adhd-med-snoozes'
// How many days of recurring medication reminders to queue ahead. Their times
// are fully known, so queuing far ahead keeps them firing even if the app
// isn't opened for a while (bounded by iOS's 64-pending cap).
const MED_SCHEDULE_DAYS = 14

function loadSnoozes() {
  try { return JSON.parse(localStorage.getItem(SNOOZE_STORAGE_KEY)) || {} } catch { return {} }
}

function saveSnoozes(snoozes) {
  try { localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(snoozes)) } catch { /* ignore */ }
}

function timeToMinutes(time24) {
  const [h, m] = time24.split(':').map(Number)
  return h * 60 + m
}

function formatTime12(time24) {
  const [h, m] = time24.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`
}

export function NotificationProvider({ schedule, tasks, medications, children }) {
  const { now: getNow } = useTime()
  const [toasts, setToasts] = useState([])
  const [notificationsEnabled, setNotificationsEnabledState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true' } catch { return false }
  })
  const [medRemindersEnabled, setMedRemindersEnabledState] = useState(() => {
    try { return localStorage.getItem(MED_STORAGE_KEY) === 'true' } catch { return false }
  })
  const [permissionState, setPermissionState] = useState('prompt')

  // Track which items we've already notified about (by schedule item key) — web only
  const notifiedRef = useRef(new Set())
  // Track the schedule date so we reset notifiedRef on day change — web only
  const scheduleDateRef = useRef(null)

  // Latest data/flags mirrored into refs so the (once-registered) app-resume
  // listener can re-queue using current values without re-subscribing.
  const latestRef = useRef({})
  latestRef.current = { schedule, tasks, medications, notificationsEnabled, medRemindersEnabled }

  // Active snoozes, mirrored into a ref so the snooze action handler and the
  // reschedule builder (both stable callbacks) always read the current map.
  const snoozesRef = useRef(loadSnoozes())

  // Check native permission state on mount
  useEffect(() => {
    if (isLocalNotificationsAvailable()) {
      checkLocalNotificationPermission().then(setPermissionState)
    } else {
      // On web, toasts don't need permission
      setPermissionState('granted')
    }
  }, [])

  // Enable/disable task notifications with permission request
  const setNotificationsEnabled = useCallback(async (enabled) => {
    if (enabled && isLocalNotificationsAvailable()) {
      const permission = await requestLocalNotificationPermission()
      setPermissionState(permission)
      if (permission !== 'granted') {
        return
      }
    }
    setNotificationsEnabledState(enabled)
    try { localStorage.setItem(STORAGE_KEY, String(enabled)) } catch { /* ignore */ }

    // If disabling and med reminders also off, cancel all
    if (!enabled && !medRemindersEnabled && isLocalNotificationsAvailable()) {
      cancelAllLocalNotifications()
    }
  }, [medRemindersEnabled])

  // Enable/disable medication reminders with permission request
  const setMedRemindersEnabled = useCallback(async (enabled) => {
    if (enabled && isLocalNotificationsAvailable()) {
      const permission = await requestLocalNotificationPermission()
      setPermissionState(permission)
      if (permission !== 'granted') {
        return
      }
    }
    setMedRemindersEnabledState(enabled)
    try { localStorage.setItem(MED_STORAGE_KEY, String(enabled)) } catch { /* ignore */ }

    // If disabling and task notifications also off, cancel all
    if (!enabled && !notificationsEnabled && isLocalNotificationsAvailable()) {
      cancelAllLocalNotifications()
    }
  }, [notificationsEnabled])

  // Add a toast with auto-dismiss
  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random()
    setToasts((prev) => [...prev, { ...toast, id }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 8000)
  }, [])

  // Dismiss a toast
  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  // Check if a subtask is done
  function isSubtaskDone(taskId, subtaskId) {
    const task = tasks.find((t) => t.id === taskId)
    if (!task) return false
    const st = task.subtasks.find((s) => s.id === subtaskId)
    return st?.done || false
  }

  // --- Native iOS: (re)build and hand the full notification set to the OS ---
  // Reads from latestRef so it's stable and safe to call from both the
  // data-change effect and the app-resume listener. Cancels + reschedules the
  // whole set each time (see scheduleAllNotifications), so it's idempotent.
  const rescheduleNativeNotifications = useCallback(async () => {
    if (!isLocalNotificationsAvailable()) return
    const {
      schedule: s, tasks: t, medications: m,
      notificationsEnabled: notifsOn, medRemindersEnabled: medsOn,
    } = latestRef.current

    if (!notifsOn && !medsOn) return

    const allNotifs = []

    // Task notifications — limited to the loaded daily schedule (today).
    if (notifsOn && s?.schedule?.length) {
      allNotifs.push(...buildTaskNotifications(s.scheduleDate, s.schedule, t))
    }

    // Medication notifications — queued MED_SCHEDULE_DAYS ahead so they keep
    // firing even if the app isn't reopened for a while.
    if (medsOn && m?.length) {
      allNotifs.push(...buildMedicationNotificationsForDays(m, new Date(), MED_SCHEDULE_DAYS))

      // Snoozed one-off reminders — persisted so they survive this rebuild.
      allNotifs.push(...buildSnoozeNotifications(m, snoozesRef.current, new Date()))

      // Drop expired snoozes so storage doesn't accumulate stale entries.
      const pruned = pruneExpiredSnoozes(snoozesRef.current, Date.now())
      if (pruned !== snoozesRef.current) {
        snoozesRef.current = pruned
        saveSnoozes(pruned)
      }
    }

    await scheduleAllNotifications(allNotifs)
  }, [])

  // Re-queue whenever the underlying data or toggles change (debounced).
  useEffect(() => {
    if (!isLocalNotificationsAvailable()) return
    if (!notificationsEnabled && !medRemindersEnabled) return
    const timer = setTimeout(() => { rescheduleNativeNotifications() }, 2000)
    return () => clearTimeout(timer)
  }, [schedule, tasks, medications, notificationsEnabled, medRemindersEnabled, rescheduleNativeNotifications])

  // Re-queue every time the app returns to the foreground, so the horizon is
  // topped up on each use regardless of whether data changed.
  useEffect(() => {
    if (!isLocalNotificationsAvailable()) return
    let remove
    import('@capacitor/app').then(({ App }) => {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) rescheduleNativeNotifications()
      }).then((handle) => { remove = handle.remove.bind(handle) })
    })
    return () => { if (remove) remove() }
  }, [rescheduleNativeNotifications])

  // --- Native iOS: register snooze buttons, foreground toast, snooze handling ---
  useEffect(() => {
    if (!isLocalNotificationsAvailable()) return

    registerMedicationActionTypes()

    addLocalNotificationListeners({
      onReceived: (notification) => {
        addToast({
          title: notification.title,
          body: notification.body,
          overdueItems: [],
        })
      },
      onActionPerformed: (event) => {
        const option = SNOOZE_OPTIONS.find((o) => o.id === event.actionId)
        const medId = event.notification?.extra?.medicationId
        if (!option || !medId) return

        const snoozes = { ...snoozesRef.current, [medId]: Date.now() + option.minutes * 60_000 }
        snoozesRef.current = snoozes
        saveSnoozes(snoozes)
        rescheduleNativeNotifications()
      },
    })
  }, [addToast, rescheduleNativeNotifications])

  // --- Web: Poll-and-fire in-app toasts ---
  useEffect(() => {
    if (isLocalNotificationsAvailable()) return // Native handles its own notifications
    if (!notificationsEnabled) return
    if (!schedule?.schedule?.length) return

    const now = getNow()
    const currentDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    const nowMinutes = now.getHours() * 60 + now.getMinutes()

    // Reset notified set when the schedule date changes
    if (scheduleDateRef.current !== schedule.scheduleDate) {
      notifiedRef.current = new Set()
      scheduleDateRef.current = schedule.scheduleDate
    }

    // Only notify for today's schedule
    if (schedule.scheduleDate !== currentDateStr) return

    const items = schedule.schedule

    // Collect overdue task items (past endTime, not done)
    const overdueItems = items.filter((item) => {
      if (item.type !== 'task') return false
      if (isSubtaskDone(item.taskId, item.subtaskId)) return false
      return nowMinutes >= timeToMinutes(item.endTime)
    })

    // Check each task item for notification trigger
    for (const item of items) {
      if (item.type !== 'task') continue

      const itemKey = `${item.time}-${item.taskId}-${item.subtaskId}`
      if (notifiedRef.current.has(itemKey)) continue

      const itemStart = timeToMinutes(item.time)
      if (nowMinutes < itemStart) continue
      if (isSubtaskDone(item.taskId, item.subtaskId)) continue

      // This item's start time has arrived — fire toast
      notifiedRef.current.add(itemKey)

      const parentTask = tasks.find((t) => t.id === item.taskId)
      const parentTitle = parentTask?.title || ''

      const otherOverdue = overdueItems.filter(
        (o) => !(o.taskId === item.taskId && o.subtaskId === item.subtaskId)
      )

      addToast({
        title: `Time to start: ${item.title}`,
        body: `${parentTitle} — until ${formatTime12(item.endTime)}`,
        overdueItems: otherOverdue.map((o) => ({
          title: o.title,
          parentTitle: tasks.find((t) => t.id === o.taskId)?.title || '',
        })),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isSubtaskDone excluded; it only reads tasks which is already in deps
  }, [schedule, tasks, getNow, addToast, notificationsEnabled])

  return (
    <NotificationContext.Provider value={{
      toasts,
      addToast,
      dismissToast,
      notificationsEnabled,
      setNotificationsEnabled,
      medRemindersEnabled,
      setMedRemindersEnabled,
      permissionState,
    }}>
      {children}
    </NotificationContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- useNotifications hook is co-located with its provider by convention
export function useNotifications() {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    return {
      toasts: [],
      addToast: () => {},
      dismissToast: () => {},
      notificationsEnabled: false,
      setNotificationsEnabled: () => {},
      medRemindersEnabled: false,
      setMedRemindersEnabled: () => {},
      permissionState: 'denied',
    }
  }
  return ctx
}
